import React from 'react';

interface BatteryIndicatorProps {
  count: string | number;
  model?: string;
  className?: string;
  size?: number;
  showPercentage?: boolean;
}

export default function BatteryIndicator({ 
  count, 
  model, 
  className = '', 
  size = 24, 
  showPercentage = false 
}: BatteryIndicatorProps) {
  // Convert battery level to number if it's a string (same as mobile app)
  const voltage = typeof count === 'string' ? parseFloat(count) : count;
  
  // Determine if it's Gen 0 model (same logic as mobile app)
  const isGen0 = model ? ["Model L", "Model H", "Model S"].includes(model) : true; // Default to Gen 0 if no model
  
  // Define thresholds for both generations (exact mobile app logic)
  const gen_0_thresholds = [
    { min: 3.79, max: 4.2, level: "full", color: "#4CAF50" },
    { min: 3.6, max: 3.79, level: "three_quarters", color: "#8BC34A" },
    { min: 3.41, max: 3.6, level: "half", color: "#FFC107" },
    { min: 3.18, max: 3.41, level: "quarter", color: "#F44336" },
    { min: 0, max: 3.18, level: "empty", color: "#F44336" },
  ];

  const gen_1_thresholds = [
    { min: 4.15, max: 4.2, level: "full", color: "#4CAF50" },
    { min: 3.75, max: 4.15, level: "three_quarters", color: "#8BC34A" },
    { min: 3.55, max: 3.75, level: "half", color: "#FFC107" },
    { min: 3.32, max: 3.55, level: "quarter", color: "#F44336" },
    { min: 0, max: 3.32, level: "empty", color: "#F44336" },
  ];

  const thresholds = isGen0 ? gen_0_thresholds : gen_1_thresholds;

  // Convert voltage to percentage and get battery config (same logic as mobile app)
  const getBatteryConfig = () => {
    if (isNaN(voltage) || voltage === 0) {
      return { percentage: 0, color: "#ccc", level: "no-data" };
    }

    // Find the appropriate threshold
    for (let i = 0; i < thresholds.length; i++) {
      if (voltage >= thresholds[i].min) {
        const threshold = thresholds[i];
        // Calculate percentage within the threshold range
        const rangeSize = threshold.max - threshold.min;
        const positionInRange = voltage - threshold.min;
        const basePercentage = i === 0 ? 80 : i === 1 ? 60 : i === 2 ? 40 : i === 3 ? 20 : 0;
        const maxPercentage = i === 0 ? 100 : i === 1 ? 80 : i === 2 ? 60 : i === 3 ? 40 : 20;
        
        const percentage = Math.min(100, Math.max(0, 
          basePercentage + (positionInRange / rangeSize) * (maxPercentage - basePercentage)
        ));

        return { 
          percentage: Math.round(percentage), 
          color: threshold.color, 
          level: threshold.level 
        };
      }
    }

    // Default case
    return { percentage: 0, color: "#F44336", level: "empty" };
  };

  const batteryConfig = getBatteryConfig();
  const { percentage, color } = batteryConfig;

  return (
    <div className={`flex items-center justify-center ${className}`} style={{ width: size, height: size }}>
      <div className="flex items-center">
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
          {/* Battery outline - simplified like mobile app */}
          <rect
            x="3"
            y="8"
            width="15"
            height="8"
            rx="2"
            ry="2"
            fill="none"
            stroke="#9CA3AF"
            strokeWidth="1.5"
          />
          
          {/* Battery terminal */}
          <rect
            x="18"
            y="11"
            width="2"
            height="2"
            rx="0.5"
            ry="0.5"
            fill="#9CA3AF"
          />
          
          {/* Battery fill based on percentage */}
          {percentage > 0 && (
            <rect
              x="4"
              y="9"
              width={Math.max(0, (13 * percentage) / 100)}
              height="6"
              rx="1"
              ry="1"
              fill={color}
            />
          )}
        </svg>
        
        {/* Show percentage if requested */}
        {showPercentage && (
          <span className="ml-1 text-xs font-medium" style={{ color }}>
            {percentage}%
          </span>
        )}
      </div>
    </div>
  );
} 